import { ActivatedRouteSnapshot, Route, Router, RouterStateSnapshot } from '@angular/router';
import { initialDataResolver } from 'app/app.resolvers';
import { AuthGuard } from 'app/core/auth/guards/auth.guard';
import { NoAuthGuard } from 'app/core/auth/guards/noAuth.guard';
import { AppOwnerGuard } from 'app/core/auth/guards/app-owner.guard';
import { LayoutComponent } from 'app/layout/layout.component';
import { AdminLayoutComponent } from './layout/admin-layout.component';
import { AccountLayoutComponent } from './layout/layouts/account-layout/account-layout.component';
import { AppLayoutComponent } from './layout/layouts/app-layout/app-layout.component';
import { ProjectLayoutComponent } from './layout/layouts/project-layout/project-layout.component';
import { environment } from 'environments/environment';
import { UserDataService } from './core/datas/user-data';
import { inject } from '@angular/core';
import { AdminService } from './core/datas/admin.service';
import { of } from 'rxjs';
import { CompanyLayoutComponent } from './layout/layouts/company-layout/company-layout.component';

// @formatter:off
/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
const siteResolver = (
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
) => {
    const _userDataSvc = inject(UserDataService);
    const _adminSvc = inject(AdminService);
    const router = inject(Router);
    let siteid = Number(route.paramMap.get('sid'));
    console.log('siteResolver siteid:', siteid);

    let site = _userDataSvc.getSelectedCompanySite(siteid);
    if (site) {
        _adminSvc.updateCompanySite(site);
    }
    return of(site);
};

const companyResolver = (
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
) => {
    const _userDataSvc = inject(UserDataService);
    const router = inject(Router);
    let companyid = Number(route.paramMap.get('cid'));
    console.log('companyResolver companyid:', companyid);

    let company = _userDataSvc.getSelectedCompany(companyid);

    if (company) {
        _userDataSvc.addSelectedCompanies(company);
    }
    return of(company);
};

export const appRoutes: Route[] = [

    // Redirect empty path to '/example'
    { path: '', pathMatch: 'full', redirectTo: `admin/projects` },

    // Redirect signed-in user to the '/example'
    //
    // After the user signs in, the sign-in page will redirect the user to the 'signed-in-redirect'
    // path. Below is another redirection for that path to redirect the user to the desired
    // location. This is a small convenience to keep all main routes together here on this file.
    // { path: 'signed-in-redirect', pathMatch: 'full', redirectTo: `a/${environment.appUUID}/projects` },
    { path: 'signed-in-redirect', pathMatch: 'full', redirectTo: `select/projects` },

    // Auth routes for guests
    {
        path: 'auth',
        canActivate: [NoAuthGuard],
        canActivateChild: [NoAuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty'
        },
        children: [
            { path: 'confirmation-required', loadChildren: () => import('app/modules/auth/confirmation-required/confirmation-required.routes') },
            { path: 'forgot-password', loadChildren: () => import('app/modules/auth/forgot-password/forgot-password.routes') },
            { path: 'reset-password', loadChildren: () => import('app/modules/auth/reset-password/reset-password.routes') },
            { path: 'sign-in', loadChildren: () => import('app/modules/auth/sign-in/sign-in.routes') },
            { path: 'sign-up', loadChildren: () => import('app/modules/auth/sign-up/sign-up.routes') }
        ]
    },

    // Selectors - Multi-step and single-entity selection flows
    {
        path: 'select',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty'
        },
        children: [
            // { path: '', loadChildren: () => import('app/modules/selections/unified-selector/unified-selector.routes') },
            { path: 'sites', loadChildren: () => import('app/modules/selections/site-selector/site-selector.routes') },
            { path: 'companies', loadChildren: () => import('app/modules/selections/company-selector/company-selector.routes') },
            { path: 'projects', loadChildren: () => import('app/modules/selections/project-selector/project-selector.routes') },
        ]
    },


    // Auth routes for authenticated users
    {
        path: '',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty'
        },
        children: [
            { path: 'sign-out', loadChildren: () => import('app/modules/auth/sign-out/sign-out.routes') },
            { path: 'unlock-session', loadChildren: () => import('app/modules/auth/unlock-session/unlock-session.routes') }
        ]
    },
    // Account routes
    {
        path: 'account',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: AccountLayoutComponent,
        resolve: {
            initialData: initialDataResolver
        },
        data: {
            layout: 'compactAcc'
        },
        children: [
            { path: 'profile', loadChildren: () => import('app/modules/account/profile/profile.routes') },
            { path: 'mycompany', loadChildren: () => import('app/modules/account/mycompany/mycompany.routes') },
            { path: 'security', loadChildren: () => import('app/modules/account/security/security.routes') },
        ]
    },
    // App routes
    {
        path: 'admin',
        canActivate: [AuthGuard, AppOwnerGuard],
        canActivateChild: [AuthGuard],
        component: AppLayoutComponent,
        resolve: {
            initialData: initialDataResolver
        },
        data: {
            layout: 'compactApp'
        },
        children: [
            { path: '', pathMatch: 'full', redirectTo: 'dashboard' },
            { path: 'dashboard', loadChildren: () => import('app/modules/app/dashboard/dashboard.routes') },
            { path: 'activity', loadChildren: () => import('app/modules/app/activity-log/activity-log.routes') },
            { path: 'projects', loadChildren: () => import('app/modules/app/projects/projects.routes') },
            { path: 'companies', loadChildren: () => import('app/modules/app/companies/app-companies.routes') },
            { path: 'code', loadChildren: () => import('app/modules/app/code/code.routes') },
            { path: 'masterdata', loadChildren: () => import('app/modules/app/masterdata/masterdata.routes') },
            { path: 'apps', loadChildren: () => import('app/modules/app/apps/apps.routes') },
            { path: 'events/trace', loadChildren: () => import('app/modules/app/traces/traces.routes') },
            { path: 'events/track', loadChildren: () => import('app/modules/app/tracks/tracks.routes') },
            { path: 'events/hierarchy', loadChildren: () => import('app/modules/app/hierarchy/hierarchy.routes') },
            { path: 'events/issues', loadChildren: () => import('app/modules/app/epcis-issues/epcis-issues.routes') },
        ]
    },
    // Project routes
    {
        path: 'p/:pid',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: ProjectLayoutComponent,
        resolve: {
            initialData: initialDataResolver
        },
        data: {
            layout: 'compact'
        },
        children: [
            { path: '', pathMatch: 'full', redirectTo: 'dashboard' },
            { path: 'dashboard', loadChildren: () => import('app/modules/project/dashboard/dashboard.routes') },
            { path: 'activity', loadChildren: () => import('app/modules/project/activity-log/activity-log.routes') },
            { path: 'detail', loadChildren: () => import('app/modules/project/detail/detail.routes') },
            { path: 'companies', loadChildren: () => import('app/modules/project/companies/companies.routes') },
            // { path: 'sites', loadChildren: () => import('app/modules/company/sites/sites.routes') },
            { path: 'code', loadChildren: () => import('app/modules/project/code/code.routes') },
            { path: 'masterdata', loadChildren: () => import('app/modules/project/masterdata/masterdata.routes') },
            { path: 'events/trace', loadChildren: () => import('app/modules/project/traces/traces.routes') },
            { path: 'events/track', loadChildren: () => import('app/modules/project/tracks/tracks.routes') },
            { path: 'events/hierarchy', loadChildren: () => import('app/modules/project/hierarchy/hierarchy.routes') },
            { path: 'events/issues', loadChildren: () => import('app/modules/project/epcis-issues/epcis-issues.routes') },
            { path: 'settings', loadChildren: () => import('app/modules/project/settings/settings.routes') },
        ]
    },
    // {
    //     path: 'p/:pid/company/:cid',
    //     canActivate: [AuthGuard],
    //     canActivateChild: [AuthGuard],
    //     component: CompanyLayoutComponent,
    //     resolve: {
    //         initialData: initialDataResolver
    //     },
    //     data: {
    //         layout: 'compact'
    //     },
    //     children: [
    //         { path: '', pathMatch: 'full', redirectTo: 'settings' },
    //         { path: 'sites', loadChildren: () => import('app/modules/company/sites/sites.routes') },
    //         { path: 'products', loadChildren: () => import('app/modules/company/products/products.routes') },
    //         { path: 'settings', loadChildren: () => import('app/modules/company/settings/settings.routes') },
    //     ]
    // },
    // Company routes (standalone)
    {
        path: 'c/:cid',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: CompanyLayoutComponent,
        resolve: {
            initialData: initialDataResolver,
            company: companyResolver
        },
        data: {
            layout: 'compact'
        },
        children: [
            { path: '', pathMatch: 'full', redirectTo: 'dashboard' },
            { path: 'dashboard', loadChildren: () => import('app/modules/company/dashboard/dashboard.routes') },
            { path: 'activity', loadChildren: () => import('app/modules/company/activity-log/activity-log.routes') },
            { path: 'detail', loadChildren: () => import('app/modules/company/detail/detail.routes') },
            { path: 'sites', loadChildren: () => import('app/modules/company/sites/sites.routes') },
            { path: 'events/trace', loadChildren: () => import('app/modules/company/traces/traces.routes') },
            { path: 'events/track', loadChildren: () => import('app/modules/company/tracks/tracks.routes') },
            { path: 'events/hierarchy', loadChildren: () => import('app/modules/company/hierarchy/hierarchy.routes') },
            { path: 'events/issues', loadChildren: () => import('app/modules/company/epcis-issues/epcis-issues.routes') },
            { path: 'codes', loadChildren: () => import('app/modules/company/codes/codes.routes') },
            // { path: 'products', loadChildren: () => import('app/modules/company/products/products.routes') },
            { path: 'products', loadChildren: () => import('app/modules/company/product-epc-class/product-epc-class.routes') },
            { path: 'epcclass', loadChildren: () => import('app/modules/company/epcclass/epcclass.routes') },
            { path: 'settings', loadChildren: () => import('app/modules/company/settings/settings.routes') },
        ]
    },

    // Site routes
    {
        path: 's/:sid',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: AdminLayoutComponent,
        resolve: {
            initialData: initialDataResolver,
            site: siteResolver
        },
        data: {
            layout: 'compact'
        },
        children: [
            { path: '', pathMatch: 'full', redirectTo: 'dashboard' },
            { path: 'dashboard', loadChildren: () => import('app/modules/admin/dashboard/dashboard.routes') },
            { path: 'activity', loadChildren: () => import('app/modules/admin/activity-log/activity-log.routes') },
            { path: 'codes', loadChildren: () => import('app/modules/admin/identifies/identify.routes') },
            { path: 'events/trace', loadChildren: () => import('app/modules/admin/traces/traces.routes') },
            { path: 'events/track', loadChildren: () => import('app/modules/admin/tracks/tracks.routes') },
            { path: 'devices', loadChildren: () => import('app/modules/admin/devices/devices.routes') },
            { path: 'events/hierarchy', loadChildren: () => import('app/modules/admin/hierarchy/hierarchy.routes') },
            { path: 'events/issues', loadChildren: () => import('app/modules/admin/epcis-issues/epcis-issues.routes') },
            { path: 'apps/distribution', loadChildren: () => import('app/modules/admin/distribution/distribution.routes') },
            { path: 'apps/products', loadChildren: () => import('app/modules/admin/products/products.routes') },
            { path: 'apps/aggregation', loadChildren: () => import('app/modules/admin/aggregation/aggregation.routes') },
            { path: 'apps/serialization', loadChildren: () => import('app/modules/admin/serialization/serialization.routes') },
            { path: 'apps/warehouse', loadChildren: () => import('app/modules/admin/warehouse/warehouse.routes') },
            { path: 'apps/inventory', loadChildren: () => import('app/modules/admin/inventory/inventory.routes') },
            { path: 'apps/urbanfarm', loadChildren: () => import('app/modules/admin/urbanfarm/urban-farm.routes') },
            { path: 'apps/transport', loadChildren: () => import('app/modules/admin/app-transport/app-transport.routes') },
            { path: 'reporting', loadChildren: () => import('app/modules/admin/reports/reports.routes') },
            // {path: 'production', loadChildren: () => import('app/modules/admin/production/po.routes')},
            // { path: 'team', loadChildren: () => import('app/modules/admin/team/team.routes') },
            { path: 'settings', loadChildren: () => import('app/modules/admin/settings/settings.routes') },
        ]
    },

    // Landing routes
    {
        path: '',
        component: LayoutComponent,
        data: {
            layout: 'empty'
        },
        children: [
            { path: 'home', loadChildren: () => import('app/modules/landing/home/home.routes') },
        ]
    },


];
